import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import './PriceHistoryChart.css';

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = {};
    payload.forEach(entry => {
      data[entry.dataKey] = entry;
    });

    const displayOrder = ['maxPrice', 'avgPrice', 'minPrice'];
    const displayed = displayOrder
      .map(key => data[key])
      .filter(item => item);

    return (
      <div className="price-history-tooltip">
        <p className="tooltip-date">{label}</p>
        {displayed.map((entry, index) => {
          const isMinPrice = entry.dataKey === 'minPrice';
          return (
            <p key={index} className={`tooltip-price ${entry.dataKey}`}>
              <span className="tooltip-label">{entry.name}:</span>
              <span className="tooltip-value">€{parseFloat(entry.value).toFixed(2)}</span>
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

export default function PriceHistoryChart({ productId, bestPrice }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stats, setStats] = useState({
    minPrice: 0,
    maxPrice: 0,
    avgPrice: 0,
    currentMinPrice: 0,
    currentAvgPrice: 0,
    priceChange: 0,
    trend: 'stable',
    minPriceDiff: { d1: null, d7: null, d30: null, d90: null },
    avgPriceDiff: { d1: null, d7: null, d30: null, d90: null }
  });

  const fetchPriceHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetch(`/api/prices/history/${productId}?days=420`);
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP Error: ${res.status}`);
      }

      const data = await res.json();
      
      if (!Array.isArray(data) || data.length === 0) {
        setChartData([]);
        setStats({
          minPrice: 0,
          maxPrice: 0,
          avgPrice: 0,
          currentMinPrice: 0,
          currentAvgPrice: 0,
          priceChange: 0,
          trend: 'stable',
          minPriceDiff: { d1: null, d7: null, d30: null, d90: null },
          avgPriceDiff: { d1: null, d7: null, d30: null, d90: null }
        });
        setLoading(false);
        return;
      }
      
      // Formatiere Daten
      const formattedData = data.map(item => ({
        date: new Date(item.date).toLocaleDateString('de-DE', { month: '2-digit', day: '2-digit' }),
        fullDate: item.date,
        avgPrice: Math.round(parseFloat(item.avgPrice) * 100) / 100,
        minPrice: Math.round(parseFloat(item.minPrice) * 100) / 100,
        maxPrice: Math.round(parseFloat(item.maxPrice) * 100) / 100,
        stores: item.stores
      }));

      setChartData(formattedData);

      // Berechne Statistiken
      const minPrices = formattedData.map(d => d.minPrice);
      const avgPrices = formattedData.map(d => d.avgPrice);
      
      const minP = Math.min(...minPrices);
      const maxP = Math.max(...minPrices);
      const avgP = avgPrices.reduce((a, b) => a + b, 0) / avgPrices.length;
      
      const currentMinPrice = minPrices[minPrices.length - 1];
      const currentAvgPrice = avgPrices[avgPrices.length - 1];
      
      // Berechne Preisunterschiede zu verschiedenen Zeitpunkten für minPrice
      // Nutze verfügbare Daten - wenn nicht genug für exakt X Tage, nutze die ältestens verfügbaren
      const getPriceDifference = (prices, daysAgo) => {
        if (prices.length < 2) return null; // Brauchen mindestens 2 Datenpunkte
        const currentPrice = prices[prices.length - 1];
        // Nutze daysAgo oder die ältestens verfügbaren Daten, je nachdem was kleiner ist
        const pastIndex = Math.max(0, prices.length - 1 - daysAgo);
        const pastPrice = prices[pastIndex];
        if (pastPrice === undefined || pastPrice === 0) return null;
        return ((currentPrice - pastPrice) / pastPrice) * 100;
      };

      const minPriceDiff1d = getPriceDifference(minPrices, 1);
      const minPriceDiff7d = getPriceDifference(minPrices, 7);
      const minPriceDiff30d = getPriceDifference(minPrices, 30);
      const minPriceDiff90d = getPriceDifference(minPrices, 90);

      // Berechne Preisunterschiede zu verschiedenen Zeitpunkten für avgPrice
      const avgPriceDiff1d = getPriceDifference(avgPrices, 1);
      const avgPriceDiff7d = getPriceDifference(avgPrices, 7);
      const avgPriceDiff30d = getPriceDifference(avgPrices, 30);
      const avgPriceDiff90d = getPriceDifference(avgPrices, 90);

      const firstPrice = minPrices[0];
      const lastPrice = minPrices[minPrices.length - 1];
      const priceChange = ((lastPrice - firstPrice) / firstPrice) * 100;

      setStats({
        minPrice: minP,
        maxPrice: maxP,
        avgPrice: avgP,
        currentMinPrice: currentMinPrice,
        currentAvgPrice: currentAvgPrice,
        priceChange: priceChange,
        trend: priceChange > 0.5 ? 'up' : priceChange < -0.5 ? 'down' : 'stable',
        minPriceDiff: { d1: minPriceDiff1d, d7: minPriceDiff7d, d30: minPriceDiff30d, d90: minPriceDiff90d },
        avgPriceDiff: { d1: avgPriceDiff1d, d7: avgPriceDiff7d, d30: avgPriceDiff30d, d90: avgPriceDiff90d }
      });

      setError('');
    } catch (err) {
      console.error('❌ Fehler beim Laden der Preishistorie:', err);
      setChartData([]);
      setError('');
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    fetchPriceHistory();
  }, [fetchPriceHistory]);

  if (loading) {
    return (
      <div className="price-history-chart-container">
        <div className="chart-loading">⏳ Preischart wird geladen...</div>
      </div>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="price-history-chart-container">
        <div className="chart-empty">📊 Keine Preishistorie verfügbar (mindestens 1 Tag Daten nötig)</div>
      </div>
    );
  }

  return (
    <div className="price-history-chart-container">
      {/* Kopfzeile mit Statistiken */}
      <div className="chart-header">
        <h3>💰 Preisverlauf (letzte 420 Tage)</h3>
        <div className="chart-stats-row">
          {/* Günstigster Preis Box mit 4 Zeitvergleichen */}
          <div className="stats-group">
            <div className="stat-header">
              <span className="stat-label">Günstigster Preis</span>
              <span className="stat-value min">€{(bestPrice || stats.currentMinPrice).toFixed(2)}</span>
            </div>
            <div className="comparisons-row">
              {stats.minPriceDiff.d1 !== null && (
                <div className={`comparison-item ${stats.minPriceDiff.d1 <= 0 ? 'positive' : 'negative'}`}>
                  <span className="comparison-label">1d</span>
                  <span className="comparison-value">{stats.minPriceDiff.d1 <= 0 ? '' : '+'}{stats.minPriceDiff.d1.toFixed(1)}%</span>
                </div>
              )}
              {stats.minPriceDiff.d7 !== null && (
                <div className={`comparison-item ${stats.minPriceDiff.d7 <= 0 ? 'positive' : 'negative'}`}>
                  <span className="comparison-label">7d</span>
                  <span className="comparison-value">{stats.minPriceDiff.d7 <= 0 ? '' : '+'}{stats.minPriceDiff.d7.toFixed(1)}%</span>
                </div>
              )}
              {stats.minPriceDiff.d30 !== null && (
                <div className={`comparison-item ${stats.minPriceDiff.d30 <= 0 ? 'positive' : 'negative'}`}>
                  <span className="comparison-label">30d</span>
                  <span className="comparison-value">{stats.minPriceDiff.d30 <= 0 ? '' : '+'}{stats.minPriceDiff.d30.toFixed(1)}%</span>
                </div>
              )}
              {stats.minPriceDiff.d90 !== null && (
                <div className={`comparison-item ${stats.minPriceDiff.d90 <= 0 ? 'positive' : 'negative'}`}>
                  <span className="comparison-label">90d</span>
                  <span className="comparison-value">{stats.minPriceDiff.d90 <= 0 ? '' : '+'}{stats.minPriceDiff.d90.toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Durchschhnitt Box mit 4 Zeitvergleichen */}
          <div className="stats-group">
            <div className="stat-header">
              <span className="stat-label">Durchschnitt</span>
              <span className="stat-value avg">€{stats.currentAvgPrice.toFixed(2)}</span>
            </div>
            <div className="comparisons-row">
              {stats.avgPriceDiff.d1 !== null && (
                <div className={`comparison-item ${stats.avgPriceDiff.d1 <= 0 ? 'positive' : 'negative'}`}>
                  <span className="comparison-label">1d</span>
                  <span className="comparison-value">{stats.avgPriceDiff.d1 <= 0 ? '' : '+'}{stats.avgPriceDiff.d1.toFixed(1)}%</span>
                </div>
              )}
              {stats.avgPriceDiff.d7 !== null && (
                <div className={`comparison-item ${stats.avgPriceDiff.d7 <= 0 ? 'positive' : 'negative'}`}>
                  <span className="comparison-label">7d</span>
                  <span className="comparison-value">{stats.avgPriceDiff.d7 <= 0 ? '' : '+'}{stats.avgPriceDiff.d7.toFixed(1)}%</span>
                </div>
              )}
              {stats.avgPriceDiff.d30 !== null && (
                <div className={`comparison-item ${stats.avgPriceDiff.d30 <= 0 ? 'positive' : 'negative'}`}>
                  <span className="comparison-label">30d</span>
                  <span className="comparison-value">{stats.avgPriceDiff.d30 <= 0 ? '' : '+'}{stats.avgPriceDiff.d30.toFixed(1)}%</span>
                </div>
              )}
              {stats.avgPriceDiff.d90 !== null && (
                <div className={`comparison-item ${stats.avgPriceDiff.d90 <= 0 ? 'positive' : 'negative'}`}>
                  <span className="comparison-label">90d</span>
                  <span className="comparison-value">{stats.avgPriceDiff.d90 <= 0 ? '' : '+'}{stats.avgPriceDiff.d90.toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={180}>
          <AreaChart
            data={chartData}
            margin={{ top: 5, right: 20, left: -20, bottom: 5 }}
          >
            <defs>
              <linearGradient id="colorMinPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorAvgPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorMaxPrice" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#2a4a5c" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 10, fill: '#7dd3fc' }}
              interval={Math.floor(chartData.length / 6)} // Zeige ca. 6-7 Labels
              height={20}
            />
            <YAxis
              tick={{ fontSize: 10, fill: '#7dd3fc' }}
              domain={[0, 'dataMax + 10']}
              width={35}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ color: '#7dd3fc', fontSize: '10px', paddingTop: '2px', margin: 0 }}
              iconType="line"
              height={20}
            />
            
            {/* Area für Mindestpreis (grün) */}
            <Area
              type="monotone"
              dataKey="minPrice"
              fill="url(#colorMinPrice)"
              stroke="#10b981"
              strokeWidth={2}
              name="Günstigster Preis"
              isAnimationActive={false}
              connectNulls
            />
            
            {/* Line für Durchschnittspreis (blau) */}
            <Line
              type="monotone"
              dataKey="avgPrice"
              stroke="#3b82f6"
              strokeWidth={2}
              name="Durchschnittspreis"
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
            
            {/* Line für Höchstpreis (rot) */}
            <Line
              type="monotone"
              dataKey="maxPrice"
              stroke="#ef4444"
              strokeWidth={2}
              name="Höchstpreis"
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Footer mit Info */}
      <div className="chart-footer">
        <p className="chart-info">
          ℹ️ Der Chart zeigt den günstigsten Tagespreis aus allen Shops in den letzten 420 Tagen.
        </p>
      </div>
    </div>
  );
}
